import { useEffect } from "react";

interface IIntersectionObserverParams {
	listRef: React.RefObject<HTMLDivElement>;
	sectionRefs: React.MutableRefObject<Map<string, HTMLDivElement | null>>;
	isClickScrolling: React.RefObject<boolean>;
	onScrollUpdateCategory: (categoryId: string) => void;
	bannerHeight: number;
	categoryListHeight: number;
	categories: Array<{ id: string }>;
}

export const useIntersectionObserver = ({
	listRef,
	sectionRefs,
	isClickScrolling,
	onScrollUpdateCategory,
	bannerHeight,
	categoryListHeight,
	categories,
}: IIntersectionObserverParams) => {
	useEffect(() => {
		const scrollRoot = listRef.current;

		if (!scrollRoot) return;

		// Dynamically calculate rootMargin to exclude Banner and CategoryList.
		// Bottom margin uses px (60% of root height at observe time) instead of a
		// percentage so Android overscroll does not drift the trigger zone.
		const topMargin = bannerHeight + categoryListHeight;
		const bottomMargin = Math.floor(scrollRoot.clientHeight * 0.6);
		const observerOptions: IntersectionObserverInit = {
			root: scrollRoot,
			rootMargin: `-${topMargin}px 0px -${bottomMargin}px 0px`,
			threshold: [0, 0.1],
		};

		const observerCallback: IntersectionObserverCallback = (entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting && !isClickScrolling.current) {
					const categoryId = (entry.target as HTMLElement).id.replace("category-", "");

					onScrollUpdateCategory(categoryId);
				}
			}
		};

		const observer = new IntersectionObserver(observerCallback, observerOptions);

		// Observe all existing sections
		for (const category of categories) {
			const element = sectionRefs.current.get(category.id);

			if (element) {
				observer.observe(element);
			}
		}

		return () => observer.disconnect();
	}, [
		listRef,
		sectionRefs,
		isClickScrolling,
		onScrollUpdateCategory,
		bannerHeight,
		categoryListHeight,
		categories,
	]);
};
